import type { Theme } from '@mui/material/styles'
import { BehaviorSubject } from 'rxjs'
import type {
  IBehaviorObservable,
  IWrappedBehaviorObservable,
} from '@/common/react/IBehaviorObservable'
import { PipedBehaviorObservable } from '@/common/react/PipedBehaviorObservable'
import { BooleanLocalStorageValue } from '@/common/local-storage/BooleanLocalStorageValue'
import { localStorageKeys } from '../configs/local-storage-keys'
import { darkTheme, lightTheme } from 'core/theme/themes.ts'
import { globalCssVars, GlobalCssVars } from 'core/theme/GlobalCssVars.ts'

export class CssThemeService {
  public readonly theme$: IBehaviorObservable<Theme>
  public readonly isLightTheme$: IWrappedBehaviorObservable<boolean>
  private themeSubject: BehaviorSubject<Theme>
  private isLightThemeLsValue = new BooleanLocalStorageValue(localStorageKeys.IsLightTheme)

  constructor() {
    this.isLightThemeLsValue.read()
    const isLightTheme = this.isLightThemeLsValue.value
    this.themeSubject = new BehaviorSubject(isLightTheme ? lightTheme : darkTheme)
    this.theme$ = this.themeSubject
    this.isLightTheme$ = new PipedBehaviorObservable(this.theme$, (theme) => {
      return theme == lightTheme
    })

    this.initCssVariables()
  }

  public switchTheme(): void {
    const isLightTheme = this.themeSubject.value == lightTheme
    const newTheme = isLightTheme ? darkTheme : lightTheme
    this.themeSubject.next(newTheme)
    this.isLightThemeLsValue.value = !isLightTheme
  }

  private parsePx(pxStr: string): number {
    return Number(pxStr.replace('px', ''))
  }

  private initCssVariables() {
    const root = document.documentElement

    const theme = this.theme$.value
    const spacingUnit = theme.spacing(1)

    root.style.setProperty('--spacing-unit', spacingUnit)
    root.style.setProperty('--color-primary-main', theme.palette.primary.main)
    root.style.setProperty('--color-primary-light', theme.palette.primary.light)
    root.style.setProperty('--color-error-main', theme.palette.error.main)

    const computed = getComputedStyle(root)

    globalCssVars.headerHeight = this.parsePx(computed.getPropertyValue('--header-height'))
    globalCssVars.radius5 = computed.getPropertyValue('--radius-5')
    globalCssVars.radius3 = computed.getPropertyValue('--radius-3')
    globalCssVars.radius7 = computed.getPropertyValue('--radius-7')
  }
}
